import axios from "axios";
// import { CognitoUserPool } from "amazon-cognito-identity-js";
import {
  api,
  monitoringApi,
  getApiBaseUrl,
  getWeatherAgentWsUrl,
} from "./api";
import { TreeFilter } from "../contexts/TreeContext";
import {
  buildMonitorAwareHierarchyFromDevices,
  type DeviceHierarchyNode,
  type DeviceMonitorSummary,
  type InstanceWithTemplateForHierarchy,
} from "../utils/deviceMonitorHierarchy";

// API Configuration
export const getWsBaseUrl = () => getWeatherAgentWsUrl();

// WeatherStack Configuration
export const WEATHERSTACK_CONFIG = {
  API_KEY: process.env.REACT_APP_WEATHERSTACK_API_KEY || "",
  API_URL:
    process.env.REACT_APP_WEATHERSTACK_API_URL || "http://api.weatherstack.com",
};

// Export all configuration
const config = {
  get API_BASE_URL() {
    return getApiBaseUrl();
  },
  get WS_BASE_URL() {
    return getWsBaseUrl();
  },
  WEATHERSTACK_CONFIG,
};

export default config;

const apiClient = api;

const GET_ALL_CACHE_TTL_MS = 10_000;
const GET_ALL_CACHE_TABLES = new Set([
  "categories",
  "events",
  "event_venue",
  "groups",
  "group_memberships",
  "instances",
  "load_assignments",
  "monitors",
  "report_definitions",
  "templates",
  "user_global_roles",
  "venues",
  "virtual_devices",
  "zones",
]);

type GetAllCacheEntry = {
  expiresAt: number;
  promise: Promise<unknown>;
};

const getAllCache = new Map<string, GetAllCacheEntry>();
const getOneCache = new Map<string, GetAllCacheEntry>();
const eventWithVenueCache = new Map<string, GetAllCacheEntry>();

const stableParamKey = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableParamKey).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableParamKey(entryValue)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const getAllCacheKey = (tableName: string, params: Record<string, any>) =>
  `${tableName}:${stableParamKey(params)}`;

const clearGetAllCache = (tableName?: string) => {
  if (!tableName) {
    getAllCache.clear();
    getOneCache.clear();
    eventWithVenueCache.clear();
    return;
  }
  for (const key of getAllCache.keys()) {
    if (key.startsWith(`${tableName}:`)) {
      getAllCache.delete(key);
    }
  }
  for (const key of getOneCache.keys()) {
    if (key.startsWith(`${tableName}:`)) {
      getOneCache.delete(key);
    }
  }
};

const clearEventWithVenueCache = (eventId?: number) => {
  if (eventId === undefined) {
    eventWithVenueCache.clear();
    return;
  }
  eventWithVenueCache.delete(String(eventId));
};

// const pool = new CognitoUserPool({
//   UserPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID || "",
//   ClientId: process.env.REACT_APP_AWS_COGNITO_WEB_CLIENT_ID || "",
// });

// function getIdToken(): Promise<string | null> {
//   const user = pool.getCurrentUser();
//   return new Promise((resolve) => {
//     if (!user) return resolve(null);
//     user.getSession((err, session) => {
//       if (err || !session?.isValid()) return resolve(null);
//       resolve(session.getIdToken().getJwtToken());
//     });
//   });
// }

export interface Instance {
  id: number | null;
  template_id: number;
  name: string;
  location_name: string;
  consumer_name: string;
  description: string;
  status: string;
  owner_group_id?: number | null;
}

export interface Template {
  id: number | null;
  category_id: number;
  name: string;
  description: string;
}

export interface Category {
  id: number | null;
  name: string;
  description: string;
}

export interface Location {
  id: number;
  name: string;
  country: string;
  region: string | null;
  lat: number;
  lon: number;
  timezone_id: string;
  utc_offset: number;
  weatherstack_id: string | null;
  status: string;
}

export interface CreateLocationInput {
  name: string;
  country: string;
  region: string | null;
  lat: number;
  lon: number;
  timezone_id: string;
  utc_offset: number;
  status: string;
}

export interface Weather {
  id: number;
  location_id: number;
  observation_time: string;
  temperature: number;
  feelslike: number;
  weather_descriptions: string;
  wind_speed: number;
  wind_degree: number;
  wind_dir: string;
  humidity: number;
  pressure: number;
  uv_index: number;
}

export interface PowerSourceTopologyEndpoint {
  virtual_device_id: number;
  virtual_device_name: string;
  zone_id?: number | null;
  zone_name?: string | null;
  event_id?: number | null;
  event_name?: string | null;
  inventory_device_id?: number | null;
  inventory_device_name?: string | null;
  location?: string | null;
}

export interface PowerSourceTopologyLink {
  id: number;
  source_virtual_device_id: number;
  downstream_virtual_device_id: number;
  relationship_type: string;
  status: string;
  notes?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  source: PowerSourceTopologyEndpoint;
  downstream: PowerSourceTopologyEndpoint;
}

export interface ZonePowerStatusBattery {
  inventory_device_id?: number | null;
  virtual_device_id?: number | null;
  name?: string | null;
  virtual_device_name?: string | null;
  soc_pct?: number | null;
  soh_pct?: number | null;
  operating_mode?: string | null;
  runtime_remaining_minutes?: number | null;
  charger_enabled?: boolean | null;
  charging_status?: string | null;
  charging_color?: string | null;
  charging_reason?: string | null;
  alarm_state?: string | null;
  battery_temperature_c?: number | null;
  ambient_temperature_c?: number | null;
  power_received_w?: number | null;
  power_supplied_w?: number | null;
  net_power_w?: number | null;
  last_reading_at?: string | null;
  last_calculated_at?: string | null;
  power_source_config_id?: number | null;
  downstream_virtual_device_ids?: number[];
}

export interface ZonePowerStatusDevice {
  virtual_device_id: number;
  virtual_device_name: string;
  inventory_device_id: number;
  inventory_device_name: string;
  category_name: string;
  role: "source" | "storage";
  status?: string | null;
}

export interface ZonePowerStatus {
  zone_id: number;
  zone_name: string;
  design_status: string;
  design_color: string;
  design_reason?: string | null;
  operating_status: string;
  operating_color: string;
  operating_reason?: string | null;
  charging_status?: string | null;
  charging_color?: string | null;
  charging_reason?: string | null;
  latest_reading_at?: string | null;
  supporting_reading_count?: number | null;
  battery_count: number;
  battery?: ZonePowerStatusBattery | null;
  power_devices?: ZonePowerStatusDevice[];
}

export interface EventZonePowerStatus {
  event_id: number;
  generated_at?: string | null;
  stale_after_minutes?: number;
  zones: ZonePowerStatus[];
}

export interface PowerSourceEnergyZone {
  zone_id: number;
  zone_name: string;
  event_id?: number | null;
  battery_inventory_device_id?: number | null;
  battery_name?: string | null;
  battery_virtual_device_id?: number | null;
  battery_virtual_device_name?: string | null;
  power_source_config_id?: number | null;
}

export interface Reading {
  id: number;
  reading_time: string;
  instance_id: number;
  template_id: number;
  template_name: string;
  instance_name: string;
  event_id?: number | null;
  event_name?: string | null;
  zone_id?: number | null;
  zone_name?: string | null;
  location_name: string;
  consumer_name: string;
  measurements: string;
  status: string;
  error_message: string;
  agent_log_id: number;
}

export interface AgentSummary {
  id: number;
  agent_key: string;
  name: string;
  enabled: boolean;
  runtime_environment?: string | null;
  host_label?: string | null;
  schedule_cron?: string | null;
  last_seen_at?: string | null;
  last_heartbeat_at?: string | null;
  notes?: string | null;
}

export interface InstanceWithTemplate {
  id: number;
  name: string;
  description: string;
  location_name?: string | null;
  consumer_name?: string | null;
  status: string;
  template_id: number;
  agent_id?: number | null;
  owner_group_id?: number | null;
  template_name: string;
  template_description: string;
  category_id?: number;
  category_name?: string;
}

export type InstanceWithTemplateAndMonitors = InstanceWithTemplateForHierarchy & {
  monitors: DeviceMonitorSummary[];
};

type TreeNode = DeviceHierarchyNode;

const buildHierarchyFromDevices = buildMonitorAwareHierarchyFromDevices;

const handleAxiosError = (error: any) => {
  const status = error.response?.status || "Unknown";
  const detail = error.response?.data?.detail || "Unknown";

  const url = error.config?.url || "Unknown";
  const baseUrl = error.config?.baseURL || getApiBaseUrl();
  const code = error.code || "Unknown";
  const msg = error.message || "Unknown";

  const message = `Status: ${status}. Detail: ${detail}.  \nBase URL: ${baseUrl}. URL: ${url}. Code: ${code}. Message: ${msg}`;

  if (axios.isAxiosError(error)) {
    console.error(`isAxiosError: true.  \n${message}`);
    throw new Error(`isAxiosError: true.  \n${message}`);
  }

  console.error(`isAxiosError: false.  \n${message}`);
  throw new Error(`isAxiosError: false.  \n${message}`);
};

export const locationApi = {
  createLocation: async (location: CreateLocationInput): Promise<Location> => {
    console.log(
      "Creating location with data:",
      JSON.stringify(location, null, 2)
    );
    try {
      const response = await apiService.create("locations", location);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        console.error("Server error response:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });
        throw new Error(
          error.response.data.detail ||
            `Server error: ${error.response.status} - ${JSON.stringify(
              error.response.data
            )}`
        );
      }
      console.error("Network or other error:", error);
      throw new Error("Failed to create location");
    }
  },

  updateLocation: async (
    id: number,
    location: CreateLocationInput
  ): Promise<Location> => {
    try {
      const response = await apiClient.put(`/locations/${id}`, location);
      return response.data;
    } catch (error) {
      console.error("Error updating location:", error);
      throw new Error("Failed to update location");
    }
  },

  updateLocationStatus: async (
    id: number,
    status: string
  ): Promise<Location> => {
    const response = await apiClient.patch(
      `/locations/${id}/status?status=${status}`
    );
    return response.data;
  },

  deleteLocation: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/locations/${id}`);
    } catch (error) {
      console.error("Error deleting location:", error);
      throw handleAxiosError(error);
    }
  },

  searchLocations: async (query: string): Promise<Location[]> => {
    const response = await apiClient.get(
      `/locations/search?query=${encodeURIComponent(query)}`
    );
    return response.data;
  },
};

export const weatherApi = {
  async getCurrentWeather(locationId: number): Promise<Weather> {
    try {
      const response = await apiClient.post(
        `/locations/${locationId}/weather/current`
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getWeatherHistory: async (
    locationId: number,
    startDate?: string,
    endDate?: string
  ): Promise<Weather[]> => {
    try {
      let url = `/locations/${locationId}/weather/history`;
      if (startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export const fetchCurrentWeather = async (
  locationId: number
): Promise<Weather> => {
  try {
    const response = await axios.post<Weather>(
      `${getApiBaseUrl()}/locations/${locationId}/weather/current`
    );
    return response.data;
  } catch (error) {
    throw handleAxiosError(error);
  }
};

export { apiClient };

export const instanceApi = {
  getAllWithTemplate: async () => {
    const response = await axios.get(
      `${getApiBaseUrl()}/instances/with-template/`
    );
    return response.data;
  },

  getAllWithTemplateAndMonitors: async (): Promise<InstanceWithTemplateAndMonitors[]> => {
    const response = await axios.get(
      `${getApiBaseUrl()}/instances/with-template-and-monitors/`
    );
    return response.data;
  },

  getWithTemplate: async (id: number) => {
    const response = await axios.get(
      `${getApiBaseUrl()}/instances/one-with-template/${id}`
    );
    return response.data;
  },
};

export const apiService = {
  getAll: async (
    tableName: string,
    filter?: Record<string, unknown>,
    sortBy?: string,
    order: "asc" | "desc" = "asc",
    offset?: number,
    limit?: number
  ) => {
    const params: Record<string, any> = { ...filter };

    if (sortBy) params.sort_by = sortBy;
    if (order) params.order = order;
    if (offset !== undefined) params.offset = offset;
    if (limit !== undefined) params.limit = limit;

    const request = async () => {
      const response = await api.get(`/api/${tableName}/`, {
        params,
      });
      return response.data;
    };

    if (!GET_ALL_CACHE_TABLES.has(tableName)) {
      return request();
    }

    const cacheKey = getAllCacheKey(tableName, params);
    const now = Date.now();
    const cached = getAllCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return cached.promise;
    }

    const promise = request().catch((error) => {
      getAllCache.delete(cacheKey);
      throw error;
    });
    getAllCache.set(cacheKey, {
      expiresAt: now + GET_ALL_CACHE_TTL_MS,
      promise,
    });
    return promise;
  },
  get: async (tableName: string, id: number) => {
    try {
      const request = async () => {
        const response = await api.get(`/api/${tableName}/${id}`);
        return response.data;
      };

      if (!GET_ALL_CACHE_TABLES.has(tableName)) {
        return await request();
      }

      const cacheKey = `${tableName}:${id}`;
      const now = Date.now();
      const cached = getOneCache.get(cacheKey);
      if (cached && cached.expiresAt > now) {
        return await cached.promise;
      }

      const promise = request().catch((error) => {
        getOneCache.delete(cacheKey);
        throw error;
      });
      getOneCache.set(cacheKey, {
        expiresAt: now + GET_ALL_CACHE_TTL_MS,
        promise,
      });
      return await promise;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  create: async (tableName: string, data: any) => {
    try {
      const response = await api.post(`/api/${tableName}/`, data);
      clearGetAllCache(tableName);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  createUnique: async (tableName: string, data: any) => {
    try {
      const response = await api.post(`/api/unique/${tableName}/`, data);
      clearGetAllCache(tableName);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  update: async (tableName: string, id: number, data: any) => {
    try {
      const response = await api.put(`/api/${tableName}/${id}`, data);
      clearGetAllCache(tableName);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  delete: async (tableName: string, id: number) => {
    try {
      const response = await api.delete(`/api/${tableName}/${id}`);
      clearGetAllCache(tableName);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export interface PowerSourceConfigSummary {
  id: number;
  inventory_device_id: number;
  status: string;
}

export const powerSourceService = {
  listConfigs: async (): Promise<PowerSourceConfigSummary[]> => {
    try {
      const response = await api.get("/power-sources/configs");
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  listEnergyZonesForProduct: async (templateId: number): Promise<PowerSourceEnergyZone[]> => {
    try {
      const response = await api.get("/power-sources/energy-zones", {
        params: { template_id: templateId },
      });
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  listEventTopology: async (eventId: number): Promise<PowerSourceTopologyLink[]> => {
    try {
      const response = await api.get(`/power-sources/events/${eventId}/topology`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  getEventZonePowerStatus: async (eventId: number): Promise<EventZonePowerStatus> => {
    try {
      const response = await api.get(`/power-sources/events/${eventId}/zone-power-status`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
  listVirtualDeviceTopology: async (virtualDeviceId: number): Promise<PowerSourceTopologyLink[]> => {
    try {
      const response = await api.get(`/power-sources/virtual-devices/${virtualDeviceId}/topology`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export const metaService = {
  updateBoundaryMeta: async (
    tableName: string,
    keyField: string,
    id: number,
    boundary: string | null,
    center?: string,
    zoom?: number,
    bearing?: number,
    pitch?: number
  ) => {
    try {
      const { data } = await api.post(`/meta/boundary/`, {
        tableName: tableName,
        keyField: keyField,
        id: id,
        boundary: boundary,
        center: center,
        zoom: zoom,
        bearing: bearing,
        pitch: pitch,
      });
      clearGetAllCache(tableName);
      if (tableName === "events") {
        clearEventWithVenueCache(id);
      }
      return data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateLocationMeta: async (
    tableName: string,
    keyField: string,
    id: number,
    location?: string
  ) => {
    try {
      const { data } = await api.post(`/meta/location/`, {
        tableName: tableName,
        keyField: keyField,
        id: id,
        location: location,
      });
      // Map content is reloaded immediately after a POI is created or moved.
      // Do not let that reload reuse the pre-write entity snapshot, otherwise
      // the newly drawn POI disappears until this cache expires.
      clearGetAllCache(tableName);
      return data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export const dataVisService = {
  getOverview: async () => {
    const response = await axios.get(`${getApiBaseUrl()}/vis/overview/`);
    return response.data;
  },

  getTreeview: async (treeType: string, treeFilter?: TreeFilter) => {
    if (treeType === "devices") {
      return agentManagementService.listMonitorAwareDeviceHierarchy();
    }
    if (treeType === "agents") {
      return agentManagementService.getAgentTreeview();
    }
    if (treeType === "unassigned-devices") {
      return agentManagementService.getUnassignedDeviceTreeview();
    }
    const { data } = await api.post(`/vis/treeview/`, {
      treeType: treeType,
      treeFilter: treeFilter ?? { tableName: "", key: "", value: "" },
    });
    return data;
  },

  getReadings: async (
    filter?: Record<string, unknown>,
    sortBy?: string,
    order: "asc" | "desc" = "asc",
    offset?: number,
    limit?: number
  ) => {
    const params: Record<string, any> = { ...filter };

    if (sortBy) params.sort_by = sortBy;
    if (order) params.order = order;
    if (offset !== undefined) params.offset = offset;
    if (limit !== undefined) params.limit = limit;

    const response = await api.get("/vis/readings/", {
      params,
    });
    return response.data;
  },
};

export interface VenueDiscoverySuggestion {
  source_provider: string;
  source_venue_id: string;
  name: string;
  street_address?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  post_code?: string | null;
  phone?: string | null;
  website_url?: string | null;
  image_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface EventDiscoverySuggestion {
  source_provider: string;
  source_event_id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  website_url?: string | null;
  image_url?: string | null;
}

export const discoveryService = {
  searchVenues: async (query: string): Promise<VenueDiscoverySuggestion[]> => {
    const response = await api.get("/discovery/venues/search", {
      params: { q: query },
    });
    return response.data.venues || [];
  },

  searchEvents: async (query: string, venueSourceId?: string | null): Promise<EventDiscoverySuggestion[]> => {
    const response = await api.get("/discovery/events/search", {
      params: {
        q: query,
        ...(venueSourceId ? { venue_source_id: venueSourceId } : {}),
      },
    });
    return response.data.events || [];
  },
};

export const agentManagementService = {
  listAgents: async (): Promise<AgentSummary[]> => {
    const response = await monitoringApi.get("/agents");
    return response.data;
  },

  listAssignedDevices: async (agentId: number): Promise<InstanceWithTemplate[]> => {
    const response = await monitoringApi.get(`/agents/${agentId}/devices`);
    return response.data;
  },

  listDevicesWithTemplate: async (): Promise<InstanceWithTemplate[]> => {
    const response = await monitoringApi.get("/agent-management/devices/with-template");
    return response.data;
  },

  listDeviceHierarchy: async (): Promise<TreeNode[]> => {
    const devices = await agentManagementService.listDevicesWithTemplate();
    return buildHierarchyFromDevices(devices);
  },

  listMonitorAwareDeviceHierarchy: async (): Promise<TreeNode[]> => {
    const devices = await instanceApi.getAllWithTemplateAndMonitors();
    return buildHierarchyFromDevices(devices);
  },

  createAgent: async (payload: { agent_key: string; name: string; enabled?: boolean }) => {
    try {
      const response = await monitoringApi.post("/agents", payload);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  assignDevice: async (agentId: number, instanceId: number) => {
    try {
      const response = await monitoringApi.put(`/agents/${agentId}/devices/${instanceId}`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  unassignDevice: async (agentId: number, instanceId: number) => {
    try {
      const response = await monitoringApi.delete(`/agents/${agentId}/devices/${instanceId}`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getAssignableDevices: async (agentId: number): Promise<InstanceWithTemplate[]> => {
    const [allDevices, agents] = await Promise.all([
      agentManagementService.listDevicesWithTemplate(),
      agentManagementService.listAgents(),
    ]);

    const assignments = await Promise.all(
      agents.map(async (agent) => ({
        agentId: agent.id,
        devices: await agentManagementService.listAssignedDevices(agent.id),
      }))
    );

    const assignedAgentIdByDeviceId = new Map<number, number>();
    assignments.forEach(({ agentId: assignedAgentId, devices }) => {
      devices.forEach((device) => {
        assignedAgentIdByDeviceId.set(device.id, assignedAgentId);
      });
    });

    return allDevices
      .filter((device) => assignedAgentIdByDeviceId.get(device.id) !== agentId)
      .sort((a, b) => formatAgentTreeDeviceLabel(a).localeCompare(formatAgentTreeDeviceLabel(b)));
  },

  getAgentTreeview: async () => {
    const [agents, devices] = await Promise.all([
      agentManagementService.listAgents(),
      instanceApi.getAllWithTemplateAndMonitors(),
    ]);

    const sortedAgents = [...agents].sort((a, b) => a.name.localeCompare(b.name));

    return sortedAgents.map((agent) => ({
      label: agent.name,
      id: `a-${agent.id}`,
      db_id: agent.id,
      node_type: "agent",
      status: agent.enabled ? "online" : "offline",
      secondary_label: agent.agent_key,
      children: buildHierarchyFromDevices(devices.filter((device) => device.agent_id === agent.id)),
    }));
  },

  getUnassignedDeviceTreeview: async () => {
    const devices = await instanceApi.getAllWithTemplateAndMonitors();
    return buildHierarchyFromDevices(devices.filter((device) => !device.agent_id));
  },
};

export const queryService = {
  instanceUpdate: async (instanceId: number) => {
    // After an instance is created or updated, refresh the query context for
    // the instance

    const response = await api.put(`/query/instance-update/${instanceId}`);

    console.log("Instance Update: ", response.data);
    return response.data;
  },

  templateUpdate: async (templateId: number) => {
    // After a template is created or updated, refresh the query context for
    // all instances that use the template

    const response = await api.put(`/query/template-update/${templateId}`);

    console.log("Template Update: ", response.data);
    return response.data;
  },

  instanceDelete: async (instanceId: number) => {
    // After an instance is deleted, delete the query context for the instance

    const response = await api.delete(`/query/instance-delete/${instanceId}`);

    console.log("Instance Delete: ", response.data);
    return response.data;
  },

  templateDelete: async (templateId: number) => {
    // After a template is deleted, delete the query context for all instances
    // that use the template

    const response = await api.delete(`/query/template-delete/${templateId}`);

    console.log("Template Delete: ", response.data);
    return response.data;
  },

  getAllContexts: async () => {
    // Get all query contexts

    const response = await api.get(`/query/all-contexts/`);

    console.log("All Contexts: ", response.data);
    return response.data;
  },

  getAllResponses: async () => {
    // Get all query responses

    const response = await api.get(`/query/all-responses/`);

    console.log("All Responses: ", response.data);
    return response.data;
  },

  clearAllContexts: async () => {
    // Clear all query contexts

    const response = await api.delete(`/query/clear-all-contexts/`);

    console.log("Clear All Contexts: ", response.data);
    return response.data;
  },

  clearAllResponses: async () => {
    // Clear all query responses

    const response = await api.delete(`/query/clear-all-responses/`);

    console.log("Clear All Responses: ", response.data);
    return response.data;
  },

  newContexts: async () => {
    // Visit all instances and generate new query_context from
    // the instance and template

    const response = await api.put(`/query/new-contexts/`);

    console.log("New Contexts: ", response.data);
    return response.data;
  },

  newResponses: async () => {
    // Visit all query_contexts and generate new query_response by
    // querying the device API

    const response = await api.put(`/query/new-responses/`);

    console.log("New Responses: ", response.data);
    return response.data;
  },

  newReadings: async () => {
    // Visit all query_response and generate readings from the
    // query_response by extracting measurements

    const response = await api.put(`/query/new-readings/`);

    console.log("New Readings: ", response.data);
    return response.data;
  },
  engineeringTest: async (tableName: string, id: number) => {
    try {
      const response = await api.put(`/query/engineering-test/${tableName}/${id}`);

      console.log("Engineering Test: ", response.data);
      return response.data;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 404 &&
        error.response?.data?.detail === "Not Found"
      ) {
        throw new Error(
          `Engineering test endpoint was not found at ${getApiBaseUrl()}. ` +
            "Switch the app data environment to the local/dev backend or restart the backend that serves this environment."
        );
      }
      throw handleAxiosError(error);
    }
  },
};

export const eventService = {
  getEventVenue: async (eventId: number) => {
    try {
      const response = await api.get(`/event/${eventId}/venue`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateEventVenue: async (eventId: number, venueId: number) => {
    try {
      const response = await api.put(`/event/${eventId}/venue`, {
        venue_id: venueId,
      });
      clearEventWithVenueCache(eventId);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getVirtualDeviceTemplates: async (virtualDeviceId: number) => {
    try {
      const response = await api.get(
        `/event/virtual_device/${virtualDeviceId}/templates`
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  refreshVirtualDeviceTemplates: async (
    virtualDeviceId: number,
    templateIds: number[]
  ) => {
    try {
      const response = await api.put(
        `/event/virtual_device/${virtualDeviceId}/templates`,
        { template_ids: templateIds }
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateVirtualDeviceBinding: async (
    virtualDeviceId: number,
    data: {
      name: string;
      description?: string | null;
      zone_id: number;
      category_id: number | null;
      allowed_category_ids?: number[];
      instance_id: number | null;
      template_ids: number[];
    }
  ) => {
    try {
      const response = await api.put(
        `/event/virtual_device/${virtualDeviceId}/binding`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  listVirtualDeviceLoadAssignments: async (virtualDeviceId: number) => {
    try {
      const response = await api.get(
        `/event/virtual_device/${virtualDeviceId}/load_assignments`
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  createVirtualDeviceLoadAssignment: async (
    virtualDeviceId: number,
    data: {
      monitor_id: number;
      load_name: string;
      inventory_device_id?: number | null;
      notes?: string | null;
      location?: string | null;
      status?: string;
      start_at?: string | null;
      end_at?: string | null;
    }
  ) => {
    try {
      const response = await api.post(
        `/event/virtual_device/${virtualDeviceId}/load_assignments`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateLoadAssignment: async (
    loadAssignmentId: number,
    data: {
      monitor_id?: number;
      load_name?: string;
      inventory_device_id?: number | null;
      notes?: string | null;
      location?: string | null;
      status?: string;
      start_at?: string | null;
      end_at?: string | null;
    }
  ) => {
    try {
      const response = await api.put(
        `/event/load_assignments/${loadAssignmentId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  listLoadAssignments: async (params: {
    monitor_id?: number;
    inventory_device_id?: number;
    status?: string;
  }) => {
    try {
      const response = await api.get("/event/load_assignments", { params });
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getEventWithVenue: async (eventId: number) => {
    try {
      const cacheKey = String(eventId);
      const now = Date.now();
      const cached = eventWithVenueCache.get(cacheKey);
      if (cached && cached.expiresAt > now) {
        return await cached.promise;
      }

      const promise = api
        .get(`/event/event_with_venue/${eventId}`)
        .then((response) => response.data)
        .catch((error) => {
          eventWithVenueCache.delete(cacheKey);
          throw error;
        });
      eventWithVenueCache.set(cacheKey, {
        expiresAt: now + GET_ALL_CACHE_TTL_MS,
        promise,
      });
      return await promise;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export interface ImagerySource {
  id: number;
  name?: string;
  mapbox_upload_id?: string;
  mapbox_tileset_id?: string;
  bounds?: any;
  created_at?: string;
}

export interface VenueImagery {
  venue_id: number;
  imagery_source_id: number;
  visible: boolean;
  opacity: number;
  z_index: number;
  name?: string;
  mapbox_upload_id?: string;
  mapbox_tileset_id?: string;
  bounds?: any;
  tileset_status?: string;
}

export interface EventImagery {
  event_id?: number;
  venue_id?: number;
  imagery_source_id: number;
  visible: boolean;
  opacity: number;
  z_index: number;
  name?: string;
  mapbox_upload_id?: string;
  mapbox_tileset_id?: string;
  bounds?: any;
  type: 'event' | 'venue';
  tileset_status?: string;
}

export interface UploadStatus {
  upload_id: string;
  progress: number;
  complete: boolean;
  error: string | null;
  tileset?: string;  // The actual tileset ID (e.g., "username.tileset_id")
}

export interface UploadedImageAsset {
  url: string;
  filename: string;
  content_type: string;
  size: number;
}

export const assetService = {
  uploadEventVenueImage: async (file: File): Promise<UploadedImageAsset> => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/assets/event-venue-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};

export const layersService = {
  addVenueImagery: async (formData: FormData) => {
    try {
      const response = await api.post('/layers/venue-imagery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  addEventImagery: async (formData: FormData) => {
    try {
      const response = await api.post('/layers/event-imagery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getVenueImagery: async (venueId: number): Promise<VenueImagery[]> => {
    try {
      const response = await api.get(`/layers/venue/${venueId}/imagery`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getEventImagery: async (eventId: number): Promise<EventImagery[]> => {
    try {
      const response = await api.get(`/layers/event/${eventId}/imagery`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateImagerySettings: async (updates: Array<{
    type: 'venue' | 'event';
    venue_id?: number;
    event_id?: number;
    imagery_source_id: number;
    visible?: boolean;
    opacity?: number;
    z_index?: number;
  }>) => {
    try {
      const response = await api.put('/layers/imagery/update', updates);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  getUploadStatus: async (uploadId: string) => {
    try {
      const response = await api.get(`/layers/upload-status/${uploadId}`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },

  updateImagerySourceStatus: async (sourceId: number, status: string, tilesetId?: string) => {
    try {
      const params = new URLSearchParams({ status });
      if (tilesetId) {
        params.append('tileset_id', tilesetId);
      }
      const response = await api.put(`/layers/imagery-source/${sourceId}/status?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw handleAxiosError(error);
    }
  },
};
